import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Backdrop,
  IconButton
} from '@mui/material';
import { apiClient } from '../../api/apiClient';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import DeleteIcon from '@mui/icons-material/Delete';

const KATEGORIEN = [
  { code: 'Stellungnahme', name: 'Stellungnahme' },
  { code: 'Gutachten', name: 'Gutachten' }
];

const BUNDESLAENDER = [
  { code: "BWB", name: "Baden-Württemberg" },
  { code: "FSB", name: "Bayern" },
  { code: "BLN", name: "Berlin" },
  { code: "BRB", name: "Brandenburg" },
  { code: "BRE", name: "Bremen" },
  { code: "HAM", name: "Hamburg" },
  { code: "HES", name: "Hessen" },
  { code: "MVP", name: "Mecklenburg-Vorpommern" },
  { code: "NSB", name: "Niedersachsen" },
  { code: "NRW", name: "Nordrhein-Westfalen" },
  { code: "RPF", name: "Rheinland-Pfalz" },
  { code: "SAA", name: "Saarland" },
  { code: "FSS", name: "Sachsen" },
  { code: "SAN", name: "Sachsen-Anhalt" },
  { code: "SHS", name: "Schleswig-Holstein" },
  { code: "FST", name: "Thüringen" },
  { code: "BRD", name: "Bundesrepublik Deutschland" }
];

export default function BerufskundeList() {
  const navigate = useNavigate();
  const [berufskunden, setBerufskunden] = useState([]);
  const [gerichte, setGerichte] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedGericht, setSelectedGericht] = useState('');
  const [selectedKategorie, setSelectedKategorie] = useState('');
  const [selectedLand, setSelectedLand] = useState('');
  const [textFilter, setTextFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Initialisierung
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const gerichteResponse = await apiClient.get('/gerichte/', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setGerichte(gerichteResponse.data);
      } catch (error) {
        console.error('Fehler beim Laden der Gerichte:', error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  // Lade Berufskunden wenn sich Filter ändern
  const fetchBerufskunden = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        skip: (page * rowsPerPage).toString(),
        limit: rowsPerPage.toString()
      });

      if (textFilter) {
        params.append('volltext', textFilter);
      }

      if (selectedGericht) {
        params.append('gericht_id', selectedGericht);
      }

      if (selectedKategorie) {
        params.append('kategorie', selectedKategorie);
      }

      if (selectedLand) {
        params.append('land', selectedLand);
      }

      const response = await apiClient.get(`/berufskunde/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setBerufskunden(response.data);
      setTotalCount(response.data.length); // Anpassen wenn Backend Pagination implementiert
    } catch (error) {
      console.error('Fehler beim Laden der Berufskunden:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, selectedGericht, selectedKategorie, selectedLand, textFilter]);

  useEffect(() => {
    fetchBerufskunden();
  }, [fetchBerufskunden]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGerichtChange = (event) => {
    setSelectedGericht(event.target.value);
    setPage(0);
  };

  const handleKategorieChange = (event) => {
    setSelectedKategorie(event.target.value);
    setPage(0);
  };

  const handleLandChange = (event) => {
    setSelectedLand(event.target.value);
    setPage(0);
  };

  const handleTextFilterChange = (event) => {
    setTextFilter(event.target.value);
    setPage(0);
  };

  const handleEdit = (berufskunde) => {
    navigate(`/admin/berufskunde/${berufskunde.id}/edit`);
  };

  const handleAddNew = () => {
    navigate('/admin/berufskunde/add');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diesen Berufskunde-Eintrag wirklich löschen?')) {
      try {
        await apiClient.delete(`/berufskunde/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        fetchBerufskunden();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Berufskunde verwalten
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Gericht</InputLabel>
          <Select
            value={selectedGericht}
            onChange={handleGerichtChange}
            label="Gericht"
          >
            <MenuItem value="">Alle</MenuItem>
            {gerichte.map((gericht) => (
              <MenuItem key={gericht.id} value={gericht.id}>
                {gericht.bezeichnung}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Kategorie</InputLabel>
          <Select
            value={selectedKategorie}
            onChange={handleKategorieChange}
            label="Kategorie"
          >
            <MenuItem value="">Alle</MenuItem>
            {KATEGORIEN.map((kategorie) => (
              <MenuItem key={kategorie.code} value={kategorie.code}>
                {kategorie.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Bundesland</InputLabel>
          <Select
            value={selectedLand}
            onChange={handleLandChange}
            label="Bundesland"
          >
            <MenuItem value="">Alle</MenuItem>
            {BUNDESLAENDER.map((land) => (
              <MenuItem key={land.code} value={land.code}>
                {land.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Textsuche"
          value={textFilter}
          onChange={handleTextFilterChange}
          sx={{ minWidth: 200 }}
        />

        <Button
          variant="contained"
          onClick={handleAddNew}
          sx={{ height: 56 }}
        >
          Neue Berufskunde
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Aktenzeichen</TableCell>
              <TableCell>Kategorie</TableCell>
              <TableCell>Gericht</TableCell>
              <TableCell>Auskunftgeber</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {berufskunden.map((berufskunde) => (
              <TableRow 
                key={berufskunde.id}
                hover
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { 
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <TableCell onClick={() => handleEdit(berufskunde)}>
                  {berufskunde.aktenzeichen}
                </TableCell>
                <TableCell onClick={() => handleEdit(berufskunde)}>
                  {berufskunde.kategorie}
                </TableCell>
                <TableCell onClick={() => handleEdit(berufskunde)}>
                  {berufskunde.gericht?.bezeichnung || '-'}
                </TableCell>
                <TableCell onClick={() => handleEdit(berufskunde)}>
                  {berufskunde.auskunftgeber}
                </TableCell>
                <TableCell onClick={() => handleEdit(berufskunde)}>
                  {format(new Date(berufskunde.datum), 'dd.MM.yyyy', { locale: de })}
                </TableCell>
                <TableCell>
                  <Button 
                    onClick={() => handleEdit(berufskunde)} 
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Bearbeiten
                  </Button>
                  <IconButton 
                    onClick={() => handleDelete(berufskunde.id)} 
                    size="small" 
                    color="error"
                    aria-label="Löschen"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Zeilen pro Seite:"
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
} 